import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Stethoscope, UserCheck, Calendar, TrendingUp, Search, Mail, Package, Edit2, X, Save } from 'lucide-react';
import { BASE_URL } from '../config';
import toast from 'react-hot-toast';

export default function DoctorsAdmin() {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });
  const [packages, setPackages] = useState([]);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchDoctors();
    fetchPackages();
  }, []);

  const fetchDoctors = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/users/role/2/`);
      if (response.ok) {
        const data = await response.json();
        setDoctors(data);
        
        // Calculate stats
        const active = data.filter(d => d.remaining_credits > 0 || d.remaining_credits === null).length;
        setStats({
          total: data.length,
          active: active,
          inactive: data.length - active,
        });
      } else {
        console.error('❌ Failed to fetch doctors:', response.statusText);
      }
    } catch (error) {
      console.error('❌ Error fetching doctors:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/packages/`);
      if (response.ok) {
        const data = await response.json();
        setPackages(data);
      }
    } catch (error) {
      console.error('❌ Error fetching packages:', error);
    }
  };

  const handleEditDoctor = (doctor) => {
    setEditingDoctor({...doctor});
    setShowEditModal(true);
  };

  const handleSaveDoctor = async () => {
    if (!editingDoctor) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${BASE_URL}/api/users/${editingDoctor.user_ID}/update-package/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          package_id: editingDoctor.package_id,
          name: editingDoctor.name,
          email: editingDoctor.email,
        }),
      });

      if (response.ok) {
        toast.success('Doctor updated successfully!');
        setShowEditModal(false);
        fetchDoctors(); // Refresh the list
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to update doctor');
      }
    } catch (error) {
      console.error('❌ Error updating doctor:', error);
      toast.error('Failed to update doctor');
    } finally {
      setSaving(false);
    }
  };

  const filteredDoctors = doctors.filter(doctor =>
    doctor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doctor.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const statCards = [
    { label: 'Total Doctors', value: stats.total, icon: Stethoscope, color: 'bg-medical-500' },
    { label: 'Active Doctors', value: stats.active, icon: UserCheck, color: 'bg-medical-600' },
    { label: 'Inactive Doctors', value: stats.inactive, icon: Calendar, color: 'bg-slate-500' },
    { label: 'Growth', value: '+18%', icon: TrendingUp, color: 'bg-medical-700' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-medical-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-sm p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">{stat.label}</p>
                <p className="text-2xl font-bold text-slate-800">{stat.value}</p>
              </div>
              <div className={`${stat.color} p-3 rounded-lg`}>
                <stat.icon className="w-6 h-6 text-slate-900" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Doctors Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white rounded-xl shadow-sm p-6"
      >
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-slate-800">All Doctors</h3>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-600 w-5 h-5" />
            <input
              type="text"
              placeholder="Search doctors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-medical-500"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Name</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Email</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Package</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Credits Used</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Remaining</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Status</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDoctors.map((doctor, index) => (
                <motion.tr
                  key={doctor.user_ID}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className="border-b border-slate-100 hover:bg-slate-50"
                >
                  <td className="py-3 px-4">
                    <div className="flex items-center">
                      <div className="w-8 h-8 rounded-full bg-medical-100 flex items-center justify-center mr-3">
                        <Stethoscope className="w-4 h-4 text-medical-600" />
                      </div>
                      <span className="text-sm font-medium text-slate-800">{doctor.name}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center text-sm text-slate-600">
                      <Mail className="w-4 h-4 mr-2 text-slate-600" />
                      {doctor.email}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center text-sm text-slate-600">
                      <Package className="w-4 h-4 mr-2 text-slate-600" />
                      {doctor.package_name || 'No Package'}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-600">
                    {doctor.credits_used?.toFixed(2) || '0.00'}
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-600">
                    {doctor.remaining_credits !== null ? doctor.remaining_credits : 'Unlimited'}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      doctor.remaining_credits > 0 || doctor.remaining_credits === null
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {doctor.remaining_credits > 0 || doctor.remaining_credits === null ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <button
                      onClick={() => handleEditDoctor(doctor)}
                      className="flex items-center gap-2 px-3 py-1.5 bg-medical-500 text-slate-900 rounded-lg hover:bg-medical-600 transition-colors text-sm font-medium"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredDoctors.length === 0 && (
          <div className="text-center py-12">
            <Stethoscope className="w-12 h-12 text-slate-700 mx-auto mb-3" />
            <p className="text-slate-500">No doctors found</p>
          </div>
        )}
      </motion.div>

      {/* Edit Doctor Modal */}
      <AnimatePresence>
        {showEditModal && editingDoctor && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowEditModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-slate-800">Edit Doctor</h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-600" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Name</label>
                  <input
                    type="text"
                    value={editingDoctor.name}
                    onChange={(e) => setEditingDoctor({...editingDoctor, name: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-medical-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={editingDoctor.email}
                    onChange={(e) => setEditingDoctor({...editingDoctor, email: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-medical-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Package</label>
                  <select
                    value={editingDoctor.package_id || ''}
                    onChange={(e) => setEditingDoctor({...editingDoctor, package_id: e.target.value})}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-medical-500"
                  >
                    <option value="">No Package</option>
                    {packages.map((pkg) => (
                      <option key={pkg.package_id} value={pkg.package_id}>
                        {pkg.package_name} - ₹{pkg.price} ({pkg.credits} credits)
                      </option>
                    ))}
                  </select>
                </div>

                <div className="bg-slate-50 p-4 rounded-lg">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-600">Current Credits:</span>
                    <span className="font-semibold text-slate-800">
                      {editingDoctor.remaining_credits !== null ? editingDoctor.remaining_credits : 'Unlimited'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Credits Used:</span>
                    <span className="font-semibold text-slate-800">
                      {editingDoctor.credits_used?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveDoctor}
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-medical-500 text-slate-900 rounded-lg hover:bg-medical-600 transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-900"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
